import { FeedData as Feed } from "@extractus/feed-extractor"
import {
  createNotesFromFileOrFeed,
  reuploadImages,
  convertLinksIntoLinkEmbeds,
} from "util/import"
import { importNoteExport } from "lib/api/notes"
import { ImportOptions } from "../types"

/**
 * Validates and processes a file or feed to create importable notes
 * @param exportFile - Optional file to process
 * @param feed - Optional feed data to process
 * @param url - URL of the feed if applicable
 * @returns Promise resolving to processed notes array or error
 */
export async function processSourceData(
  exportFile: File | null,
  feed: Feed | null,
  url: string | null
) {
  // Check file type for proper validation
  if (exportFile) {
    const validFileTypes = [
      "text/xml",
      "application/zip",
      "application/x-zip-compressed",
      "application/octet-stream",
    ]
    if (!validFileTypes.includes(exportFile.type)) {
      throw `Invalid file type. Please upload an XML or ZIP file. Current type: ${exportFile.type || "unknown"}`
    }

    // Check if file has some content
    if (exportFile.size === 0) {
      throw "The uploaded file is empty. Please try again with a valid export file."
    }
  }

  // Process the file or feed
  const processedNotes = await createNotesFromFileOrFeed(
    exportFile,
    feed,
    url || ""
  )

  if (!processedNotes || processedNotes.length === 0) {
    if (exportFile) {
      throw "No posts found in the uploaded file. If you've uploaded a Substack export, ensure it contains posts.csv."
    } else {
      throw "No posts found at this URL. Please check that you've entered a valid RSS feed URL."
    }
  }

  console.log(
    `Successfully processed ${processedNotes.length} notes from ${exportFile ? "file" : "feed"}`
  )

  return processedNotes
}

/**
 * Process a single post for import
 * @param post - The post object to import
 * @param importOptions - Import options like overwrite and storeOnArweave
 * @returns Promise resolving to import result
 */
async function importSinglePost(post: any, importOptions: ImportOptions) {
  if (!post) return { id: "FAILURE", error: "Post is undefined" }

  try {
    // Check for valid post data to avoid "Cannot read properties of undefined" errors
    if (post.json === undefined && post.staticHtml === undefined) {
      console.warn(
        `Skipping invalid post: ${post?.title || "untitled"} - missing content`
      )
      return {
        id: "FAILURE",
        error: "Invalid post data",
        post: {
          title: post?.title?.substring(0, 40) || "Untitled post",
          reason: "Invalid post data or missing content",
        },
      }
    }

    // Safely handle cover image that might be undefined
    const coverImgSrc = post.cover_img?.img?.src

    // Reupload images to our CDN if applicable
    try {
      await reuploadImages(post, coverImgSrc)
    } catch (imgErr) {
      console.warn(
        `Error reuploading images for ${post.title || "untitled"}:`,
        imgErr
      )
      // Continue with import even if image upload fails
    }

    // Convert links to embeds when appropriate
    try {
      await convertLinksIntoLinkEmbeds(post)
    } catch (linkErr) {
      console.warn(
        `Error converting links for ${post.title || "untitled"}:`,
        linkErr
      )
      // Continue with import even if link conversion fails
    }

    // Call the import API endpoint
    const title = post.title || "untitled"
    console.log(`Importing post: ${title}`)
    return await importNoteExport(post, {
      overwrite: importOptions.overwrite,
      storeOnArweave: importOptions.storeOnArweave,
    })
  } catch (err) {
    console.error(`Error importing post "${post?.title || "untitled"}":`, err)

    // Extract meaningful error message
    let errorReason = "Unknown error"
    if (err instanceof Error) {
      errorReason = err.message
    } else if (typeof err === "string") {
      errorReason = err
    }

    return {
      id: "FAILURE",
      error: errorReason,
      post: {
        title: post?.title?.substring(0, 40) || "Untitled post",
        reason: errorReason,
      },
    }
  }
}

/**
 * Processes a batch of posts in parallel
 * @param posts - Array of posts to process
 * @param importOptions - Import options
 * @param batchSize - Number of posts to process in parallel
 * @param onProgress - Callback for tracking progress
 * @returns Promise resolving to final import results
 */
export async function processBatchedPosts(
  posts: any[],
  importOptions: ImportOptions,
  batchSize = 5,
  onProgress: (results: {
    successful: any[]
    skipped: any[]
    errors: any[]
  }) => void
) {
  let position = 0
  let successful: any[] = []
  let skipped: any[] = []
  let errors: any[] = []

  console.log(
    `Starting batch import of ${posts.length} posts with batch size ${batchSize}`
  )

  while (position < posts.length) {
    const batch = posts.slice(position, position + batchSize)
    console.log(
      `Processing batch ${Math.floor(position / batchSize) + 1} of ${Math.ceil(posts.length / batchSize)}`
    )

    // Process each post in the batch in parallel
    const results = await Promise.all(
      batch.map((post) => importSinglePost(post, importOptions))
    )

    // Process results
    results.forEach((result, index) => {
      const post = batch[index]
      const title = post?.title?.substring(0, 40) || "Untitled post"

      if (result.id === "ALREADY_EXISTS") {
        skipped.push({
          title,
          reason: "Already exists",
        })
      } else if (result.id === "FAILURE") {
        // Type guard to check if result has post property
        if ("post" in result && result.post) {
          errors.push(result.post)
        } else {
          // If no post property, use the error property if available
          errors.push({
            title,
            reason:
              "error" in result ? result.error : "API error during import",
          })
        }
      } else {
        successful.push(post)
      }
    })

    // Update progress
    onProgress({ successful, skipped, errors })

    // Move to next batch
    position += batchSize
  }

  return { successful, skipped, errors }
}
